import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  UserCheck,
  Settings,
  TrendingUp,
  Users,
  Activity
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import resourceRoleMasterService from '../../services/resourceRoleMasterService'
import RevisionHistoryModal from '../../components/RevisionHistoryModal/RevisionHistoryModal'
import '../../components/RevisionHistoryModal/RevisionHistoryModal.css'
import '../../components/ConfirmationModal/ConfirmationModal.css'

const ResourceRoleMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalResourceRoles: 0,
    activeResourceRoles: 0,
    inactiveResourceRoles: 0,
    technicalRoles: 0,
    managementRoles: 0,
    designRoles: 0,
    qaRoles: 0
  })
  const [showRevisionHistory, setShowRevisionHistory] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [selectedResourceRoleData, setSelectedResourceRoleData] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [resourceRolesResponse, statsResponse] = await Promise.all([
        resourceRoleMasterService.getAllResourceRoleMasters(),
        resourceRoleMasterService.getResourceRoleMasterStats()
      ])
      
      if (resourceRolesResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (resourceRolesResponse.data || []).map(resourceRole => ({
          ...resourceRole,
          id: resourceRole._id,
          formattedExperience: resourceRole.minimumExperience && resourceRole.maximumExperience ? 
            `${resourceRole.minimumExperience} - ${resourceRole.maximumExperience} years` : 
            'Not Set',
          formattedBillableRate: resourceRole.billableRate ? 
            `$${resourceRole.billableRate}/hr` : 
            'Not Set',
          formattedRequiredSkills: resourceRole.requiredSkills?.map(skill => skill.skillName).join(', ') || 'Not Set',
          currentVersion: `Rev ${resourceRole.revisionHistory?.length + 1 || 1}`
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (resourceRolesResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No Resource Role Master records found. Create your first Resource Role!')
        }
      } else {
        toast.error(resourceRolesResponse.message || 'Failed to load Resource Role Master records')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading Resource Role Master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view Resource Role Master records')
      } else {
        toast.error('Failed to load Resource Role Master records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      key: 'resourceRoleCode',
      label: 'Resource Role Code',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <UserCheck size={14} className="text-blue-600" />
          </div>
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      )
    },
    {
      key: 'resourceRoleName',
      label: 'Resource Role Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          {row.resourceRoleDescription && (
            <div className="text-sm text-gray-500">{row.resourceRoleDescription}</div>
          )}
        </div>
      )
    },
    {
      key: 'roleCategory',
      label: 'Category',
      sortable: true,
      render: (value) => {
        const categoryColors = {
          'Technical': 'bg-blue-100 text-blue-800',
          'Management': 'bg-green-100 text-green-800',
          'Design': 'bg-purple-100 text-purple-800',
          'Quality Assurance': 'bg-yellow-100 text-yellow-800',
          'DevOps': 'bg-red-100 text-red-800',
          'Business': 'bg-indigo-100 text-indigo-800',
          'Support': 'bg-gray-100 text-gray-800',
          'Other': 'bg-gray-100 text-gray-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${categoryColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'formattedExperience',
      label: 'Experience',
      sortable: true,
      render: (value) => (
        <div className="text-sm text-gray-600">
          {value}
        </div>
      )
    },
    {
      key: 'resourceRoleStatus',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value}
        </span>
      )
    }
  ]

  const handleAddNew = () => {
    navigate('/projects/resource-role-master/new')
  }

  const handleEdit = (row) => {
    navigate('/projects/resource-role-master/edit', { 
      state: { resourceRoleData: row } 
    })
  }

  const handleView = (row) => {
    navigate(`/projects/resource-role-master/view/${row.id}`)
  }

  const handleDelete = (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await resourceRoleMasterService.deleteResourceRoleMaster(deleteItem.id)
      if (response.success) {
        toast.success('Resource Role Master deleted successfully')
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete Resource Role Master')
      }
    } catch (error) {
      console.error('Error deleting Resource Role Master:', error)
      toast.error('Failed to delete Resource Role Master')
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleRevisionHistory = (row) => {
    setSelectedResourceRoleData(row)
    setShowRevisionHistory(true)
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Projects',
      path: '/projects',
      onClick: () => navigate('/projects')
    },
    {
      label: 'Masters',
      path: '/projects?tab=masters',
      onClick: () => navigate('/projects?tab=masters')
    },
    {
      label: 'Resource Role Master',
      path: '/projects/resource-role-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Resource Role Master</h1>
          <p>Manage resource roles and their specifications for your organization</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAddNew}
          >
            <Plus size={16} />
            Add New
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <UserCheck size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalResourceRoles}</div>
          <div className="metric-title">Total Resource Roles</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Settings size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeResourceRoles}</div>
          <div className="metric-title">Active Resource Roles</div>
          <div className="metric-subtitle">Currently tracking</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Activity size={16} />
            </div>
            <div className="metric-trend neutral">
              <TrendingUp size={12} />
              <span>Stable</span>
            </div>
          </div>
          <div className="metric-value">{stats.technicalRoles}</div>
          <div className="metric-title">Technical Roles</div>
          <div className="metric-subtitle">Technical positions</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+15%</span>
            </div>
          </div>
          <div className="metric-value">{stats.managementRoles}</div>
          <div className="metric-title">Management Roles</div>
          <div className="metric-subtitle">Leadership positions</div>
        </div>
      </div>

      {/* Resource Role Master Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onRevisionHistory={handleRevisionHistory}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search resource roles..."
          emptyMessage="No resource roles found. Create your first resource role!"
          noCard={true}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Resource Role Master"
        message={`Are you sure you want to delete "${deleteItem?.resourceRoleName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Revision History Modal */}
      <RevisionHistoryModal
        isOpen={showRevisionHistory}
        onClose={() => setShowRevisionHistory(false)}
        data={selectedResourceRoleData}
        title="Resource Role Master Revision History"
      />
    </div>
  )
};

export default ResourceRoleMaster;
